import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone } from "lucide-react";

export default function ConsultationPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/consultations", form);
      setDone(true);
      toast.success("Consultation request sent — we'll be in touch within one business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="consultation-page" className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16">
      <div>
        <p className="text-xs tracking-[0.4em] uppercase text-brand mb-4">Book a Consultation</p>
        <h1 className="font-display text-6xl sm:text-7xl text-white leading-[0.95]">Let's build your rollout.</h1>
        <p className="text-sm md:text-base text-zinc-400 mt-6 leading-relaxed max-w-md">
          Tell us who you are and what you're planning. Benj or a member of the team will reach out within one business day.
        </p>
        <a data-testid="consultation-phone-link" href="tel:3234947199"
          className="inline-flex items-center gap-3 mt-10 border border-white/15 hover:border-brand px-6 py-4 text-zinc-200 hover:text-white transition-colors">
          <Phone className="w-4 h-4 text-brand" />
          <span className="font-display text-2xl tracking-wide">323-494-7199</span>
        </a>
      </div>
      <div className="border border-white/10 bg-[#0A0A0A] p-8">
        {done ? (
          <div data-testid="consultation-success" className="py-16 text-center">
            <h2 className="font-display text-4xl text-white">Request received.</h2>
            <p className="text-sm text-zinc-400 mt-4">Check your inbox — a confirmation email is on its way.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs text-zinc-400">Artist / Brand Name *</Label>
              <Input data-testid="consultation-name" required value={form.name} onChange={set("name")} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs text-zinc-400">Email *</Label>
                <Input data-testid="consultation-email" required type="email" value={form.email} onChange={set("email")} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Phone *</Label>
                <Input data-testid="consultation-phone-input" required value={form.phone} onChange={set("phone")} className="bg-white/5 border-white/10 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">I'm interested in</Label>
              <Select value={form.service_interest} onValueChange={set("service_interest")}>
                <SelectTrigger data-testid="consultation-interest" className="bg-white/5 border-white/10 mt-1">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="models">Models</SelectItem>
                  <SelectItem value="cars">Cars</SelectItem>
                  <SelectItem value="houses">Houses</SelectItem>
                  <SelectItem value="marketing">Music Marketing</SelectItem>
                  <SelectItem value="other">Something else</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Tell us about the project</Label>
              <Textarea data-testid="consultation-message" rows={5} value={form.message} onChange={set("message")} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <button data-testid="consultation-submit-btn" type="submit" disabled={loading}
              className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 text-white text-sm font-semibold tracking-widest uppercase py-4 transition-colors">
              {loading ? "Sending…" : "Request Consultation"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
